// src/hooks/api.ts - Fixed for admin_users/game_hosts structure
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {supabase, handleSupabaseError} from '@/services/supabase';
import toast from 'react-hot-toast';
import type {Database} from '@/types/supabase';

// A host row flattened with its account (Tier 2) and organization (Tier 1) plus
// the precomputed effective_active / access_status cascade. Sourced from the
// game_hosts_with_access view (see the matching migration).
export type GameHostWithAccess = Database['public']['Views']['game_hosts_with_access']['Row'];
import type {
    Organization,
    Account,
    GameSession,
    AdminUser,
    CreateOrganizationData,
    UpdateOrganizationData,
    CreateAccountData,
    UpdateAccountData,
    OrganizationFilters,
    AccountFilters,
    GameHostFilters,
    GameSessionFilters,
    PaginatedResponse,
    DashboardKPI,
} from '@/types';

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

// Helper function to check if account is expired
// const calculateIsExpired = (accountExpirationDate: string | null | undefined): boolean => {
//     if (!accountExpirationDate) return false;
//     try {
//         const expirationDate = new Date(accountExpirationDate);
//         const today = new Date();
//         return expirationDate < today;
//     } catch {
//         return false;
//     }
// };

// =====================================================
// DASHBOARD KPIs
// =====================================================

export const useDashboardKPIs = () => {
    return useQuery({
        queryKey: ['dashboard-kpis'],
        queryFn: async (): Promise<DashboardKPI> => {
            console.log('🔍 Fetching dashboard KPIs...');

            try {
                // Fetch all counts in parallel
                const [
                    organizationsResult,
                    accountsResult,
                    hostsResult,
                    sessionsResult
                ] = await Promise.all([
                    supabase.from('organizations').select('id', {count: 'exact', head: true}),
                    supabase.from('accounts').select('id', {count: 'exact', head: true}),
                    supabase.from('game_hosts').select('id, invitation_status').is('deleted_at', null),
                    supabase.from('game_sessions').select('id, status', {count: 'exact'})
                ]);

                if (organizationsResult.error) throw organizationsResult.error;
                if (accountsResult.error) throw accountsResult.error;
                if (hostsResult.error) throw hostsResult.error;
                if (sessionsResult.error) throw sessionsResult.error;

                const totalOrganizations = organizationsResult.count || 0;
                const totalAccounts = accountsResult.count || 0;
                const hosts = hostsResult.data || [];
                const sessions = sessionsResult.data || [];

                // Calculate host statistics based on invitation_status
                const totalHosts = hosts.length;
                const registeredHosts = hosts.filter(h => h.invitation_status === 'accepted').length;
                const pendingInvitations = hosts.filter(h => h.invitation_status !== 'accepted').length;

                // Calculate game session statistics
                const totalGames = sessions.length;
                const liveGames = sessions.filter(s => s.status === 'live').length;
                const activeGames = sessions.filter(s => ['live', 'created', 'paused'].includes(s.status)).length;
                const completedGames = sessions.filter(s => s.status === 'completed').length;

                const kpis: DashboardKPI = {
                    totalOrganizations,
                    totalAccounts,
                    totalGameHosts: totalHosts,
                    registeredHosts,
                    pendingInvitations,
                    liveGames,
                    activeGames,
                    completedGames,
                    totalGames,
                };

                console.log('✅ Dashboard KPIs loaded:', kpis);
                return kpis;
            } catch (error: any) {
                console.error('❌ Failed to fetch dashboard KPIs:', error);
                throw new Error(`Failed to fetch dashboard data: ${error.message}`);
            }
        },
        staleTime: 30 * 1000, // 30 seconds
        refetchInterval: 60 * 1000, // 1 minute
    });
};

// =====================================================
// ORGANIZATIONS HOOKS (formerly districts)
// =====================================================

export const useOrganizations = (
    page: number = 1,
    pageSize: number = 25,
    filters?: OrganizationFilters,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
) => {
    return useQuery({
        queryKey: ['organizations', page, pageSize, filters, sortBy, sortOrder],
        queryFn: async (): Promise<PaginatedResponse<Organization>> => {
            try {
                let query = supabase
                    .from('organizations')
                    .select('*', {count: 'exact'});

                // Apply filters
                if (filters?.account_type) {
                    query = query.eq('account_type', filters.account_type);
                }

                if (filters?.state) {
                    query = query.eq('state', filters.state);
                }

                if (filters?.status) {
                    query = query.eq('status', filters.status);
                }

                if (filters?.search) {
                    query = query.or(`name.ilike.%${filters.search}%,administrator_name.ilike.%${filters.search}%,administrator_email.ilike.%${filters.search}%`);
                }

                if (filters?.expired !== undefined) {
                    if (filters.expired) {
                        query = query.lt('account_expiration_date', new Date().toISOString());
                    } else {
                        query = query.or('account_expiration_date.is.null,account_expiration_date.gte.' + new Date().toISOString());
                    }
                }

                // Apply sorting
                if (sortBy && sortOrder) {
                    query = query.order(sortBy, {ascending: sortOrder === 'asc'});
                } else {
                    query = query.order('created_at', {ascending: false});
                }

                // Apply pagination
                const start = (page - 1) * pageSize;
                query = query.range(start, start + pageSize - 1);

                const {data, error, count} = await query;

                if (error) {
                    handleSupabaseError(error, 'useOrganizations');
                }

                return {
                    data: data || [],
                    total: count || 0,
                    page,
                    pageSize,
                    totalPages: Math.ceil((count || 0) / pageSize)
                };
            } catch (error) {
                console.error('Organizations fetch failed:', error);
                throw error;
            }
        },
        staleTime: 30 * 1000,
    });
};

// =====================================================
// ACCOUNTS HOOKS (formerly schools)
// =====================================================

export const useAccounts = (
    page: number = 1,
    pageSize: number = 25,
    filters?: AccountFilters,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
) => {
    return useQuery({
        queryKey: ['accounts', page, pageSize, filters, sortBy, sortOrder],
        queryFn: async (): Promise<PaginatedResponse<Account>> => {
            try {
                let query = supabase
                    .from('accounts')
                    .select(`
                        *,
                        organizations!inner(name, state)
                    `, {count: 'exact'});

                // Apply filters
                if (filters?.account_type) {
                    query = query.eq('account_type', filters.account_type);
                }

                if (filters?.organization_id) {
                    query = query.eq('organization_id', filters.organization_id);
                }

                if (filters?.state) {
                    query = query.eq('state', filters.state);
                }

                if (filters?.status) {
                    query = query.eq('status', filters.status);
                }

                if (filters?.search) {
                    query = query.or(`name.ilike.%${filters.search}%,contact_person_name.ilike.%${filters.search}%,contact_person_email.ilike.%${filters.search}%,city.ilike.%${filters.search}%`);
                }

                if (filters?.expired !== undefined) {
                    if (filters.expired) {
                        query = query.lt('account_expiration_date', new Date().toISOString());
                    } else {
                        query = query.or('account_expiration_date.is.null,account_expiration_date.gte.' + new Date().toISOString());
                    }
                }

                // Apply sorting
                if (sortBy && sortOrder) {
                    query = query.order(sortBy, {ascending: sortOrder === 'asc'});
                } else {
                    query = query.order('created_at', {ascending: false});
                }

                // Apply pagination
                const start = (page - 1) * pageSize;
                query = query.range(start, start + pageSize - 1);

                const {data, error, count} = await query;

                if (error) {
                    handleSupabaseError(error, 'useAccounts');
                }

                return {
                    data: data || [],
                    total: count || 0,
                    page,
                    pageSize,
                    totalPages: Math.ceil((count || 0) / pageSize)
                };
            } catch (error) {
                console.error('Accounts fetch failed:', error);
                throw error;
            }
        },
        staleTime: 30 * 1000,
    });
};

// =====================================================
// GAME HOSTS HOOKS (UPDATED FOR NEW STRUCTURE)
// =====================================================

export const useGameHosts = (
    page: number = 1,
    pageSize: number = 25,
    filters?: GameHostFilters,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
) => {
    return useQuery({
        queryKey: ['game-hosts', page, pageSize, filters, sortBy, sortOrder],
        queryFn: async (): Promise<PaginatedResponse<GameHostWithAccess>> => {
            try {
                // Read from the game_hosts_with_access view: game_hosts flattened
                // with account/organization fields and the precomputed
                // effective_active / access_status cascade, so every filter and
                // sort is a plain column (no embedded join paths in the client).
                // The shared client is typed against the hand-written Database
                // (full generated-types adoption is a separate cleanup), which
                // doesn't include this view yet, so access it untyped here and
                // shape the result via GameHostWithAccess.
                let query: any = (supabase as any)
                    .from('game_hosts_with_access')
                    .select('*', {count: 'exact'});

                if (filters?.account_type) {
                    query = query.eq('account_type', filters.account_type);
                }
                if (filters?.account_id) {
                    query = query.eq('account_id', filters.account_id);
                }
                if (filters?.organization_id) {
                    query = query.eq('organization_id', filters.organization_id);
                }

                // Effective-access cascade (host -> account -> organization),
                // precomputed by the view.
                if (filters?.access === 'active') {
                    query = query.eq('effective_active', true);
                } else if (filters?.access) {
                    query = query.eq('access_status', filters.access);
                }

                if (filters?.status) {
                    query = query.eq('status', filters.status);
                }
                if (filters?.invitation_status) {
                    query = query.eq('invitation_status', filters.invitation_status);
                }
                if (filters?.registered !== undefined) {
                    query = filters.registered
                        ? query.eq('invitation_status', 'accepted')
                        : query.neq('invitation_status', 'accepted');
                }
                if (filters?.search) {
                    query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
                }

                // Exclude soft-deleted hosts
                query = query.is('deleted_at', null);

                // Every displayed column lives on the view, so sort server-side.
                query = query.order(sortBy || 'created_at', {
                    ascending: sortBy ? sortOrder === 'asc' : false,
                });

                const start = (page - 1) * pageSize;
                query = query.range(start, start + pageSize - 1);

                const {data, error, count} = await query;
                if (error) {
                    handleSupabaseError(error, 'useGameHosts');
                }

                return {
                    data: data || [],
                    total: count || 0,
                    page,
                    pageSize,
                    totalPages: Math.ceil((count || 0) / pageSize)
                };
            } catch (error: any) {
                console.error('Game Hosts fetch failed:', error);
                throw error;
            }
        },
        staleTime: 30 * 1000,
    });
};

// =====================================================
// ADMIN USERS HOOKS (NEW)
// =====================================================

export const useAdminUsers = () => {
    return useQuery({
        queryKey: ['admin-users'],
        queryFn: async (): Promise<AdminUser[]> => {
            const {data, error} = await supabase
                .from('admin_users')
                .select(`
                    *,
                    organization:organizations(name)
                `)
                .order('created_at', {ascending: false});

            if (error) {
                handleSupabaseError(error, 'useAdminUsers');
            }

            return data || [];
        },
        staleTime: 30 * 1000,
    });
};

// =====================================================
// GAME SESSIONS HOOKS
// =====================================================

export const useGameSessions = (
    page: number = 1,
    pageSize: number = 25,
    filters?: GameSessionFilters,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
) => {
    return useQuery({
        queryKey: ['game-sessions', page, pageSize, filters, sortBy, sortOrder],
        queryFn: async (): Promise<PaginatedResponse<GameSession>> => {
            try {
                let query = supabase
                    .from('game_sessions')
                    .select(`
                        *,
                        game_hosts!inner(
                            id,
                            first_name,
                            last_name,
                            email,
                            accounts!inner(
                                id,
                                name,
                                organizations!inner(name)
                            )
                        )
                    `, {count: 'exact'});

                // Apply filters
                if (filters?.game_host_id) {
                    query = query.eq('game_host_id', filters.game_host_id);
                }

                if (filters?.status) {
                    query = query.eq('status', filters.status);
                }

                if (filters?.game_name) {
                    query = query.eq('game_name', filters.game_name);
                }

                if (filters?.date_from) {
                    query = query.gte('created_at', filters.date_from);
                }

                if (filters?.date_to) {
                    query = query.lte('created_at', filters.date_to);
                }

                // Apply sorting
                if (sortBy && sortOrder) {
                    query = query.order(sortBy, {ascending: sortOrder === 'asc'});
                } else {
                    query = query.order('created_at', {ascending: false});
                }

                // Apply pagination
                const start = (page - 1) * pageSize;
                query = query.range(start, start + pageSize - 1);

                const {data, error, count} = await query;

                if (error) {
                    handleSupabaseError(error, 'useGameSessions');
                }

                return {
                    data: data || [],
                    total: count || 0,
                    page,
                    pageSize,
                    totalPages: Math.ceil((count || 0) / pageSize)
                };
            } catch (error: any) {
                console.error('Game Sessions fetch failed:', error);
                throw error;
            }
        },
        staleTime: 30 * 1000,
    });
};

// =====================================================
// MUTATION HOOKS - CREATE/UPDATE/DELETE
// =====================================================

export const useCreateOrganization = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateOrganizationData): Promise<Organization> => {
            const {data: result, error} = await supabase
                .from('organizations')
                .insert(data)
                .select()
                .single();

            if (error) {
                handleSupabaseError(error, 'useCreateOrganization');
            }

            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            queryClient.invalidateQueries({queryKey: ['dashboard-kpis']});
            toast.success('Organization created successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to create organization');
        }
    });
};

export const useUpdateOrganization = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({id, data}: { id: string; data: UpdateOrganizationData }): Promise<Organization> => {
            const {data: result, error} = await supabase
                .from('organizations')
                .update(data)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                handleSupabaseError(error, 'useUpdateOrganization');
            }

            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            toast.success('Organization updated successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to update organization');
        }
    });
};

export const useDeleteOrganization = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const {error} = await supabase
                .from('organizations')
                .delete()
                .eq('id', id);

            if (error) {
                handleSupabaseError(error, 'useDeleteOrganization');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['organizations']});
            queryClient.invalidateQueries({queryKey: ['dashboard-kpis']});
            toast.success('Organization deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to delete organization');
        }
    });
};

// =====================================================
// ACCOUNT MUTATIONS
// =====================================================

export const useCreateAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateAccountData): Promise<Account> => {
            const {data: result, error} = await supabase
                .from('accounts')
                .insert(data)
                .select()
                .single();

            if (error) {
                handleSupabaseError(error, 'useCreateAccount');
            }

            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            queryClient.invalidateQueries({queryKey: ['dashboard-kpis']});
            toast.success('Account created successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to create account');
        }
    });
};

export const useUpdateAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({id, data}: { id: string; data: UpdateAccountData }): Promise<Account> => {
            const {data: result, error} = await supabase
                .from('accounts')
                .update(data)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                handleSupabaseError(error, 'useUpdateAccount');
            }

            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            toast.success('Account updated successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to update account');
        }
    });
};

export const useDeleteAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const {error} = await supabase
                .from('accounts')
                .delete()
                .eq('id', id);

            if (error) {
                handleSupabaseError(error, 'useDeleteAccount');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            queryClient.invalidateQueries({queryKey: ['dashboard-kpis']});
            toast.success('Account deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to delete account');
        }
    });
};
